<template>
  <div v-if="isReady" class="drop-down clearfix" @click.stop>
    <div
      class="list__box list__box__domain"
      :class="{ large: wideColumnData['domain'] }"
      @mouseenter="setWideColumn('domain')"
    >
      <div class="list__box-title">{{ $t('common.domain') }}</div>
      <div class="list--wrapper" ref="domainList" @scroll.stop="pageListScroll('domain')">
        <ul class="list__box-item">
          <li
            v-for="item in domains"
            :key="item"
            class="domain-list-item list-item"
            :class="{ on: item === activeDomain }"
            @click="setActiveDomain(item)">
            <p class="item-domain">{{ item }}<i v-show="item === activeDomain" class="icon icon--page-list-next"></i></p>
          </li>
        </ul>
        <i v-if="domains.length > 8 && gradient.domain" class="background-gradient"></i>
      </div>
    </div>
    <div
      class="list__box list__box_sitecode"
      :class="{ large: wideColumnData['sitecode'] }"
      @mouseenter="setWideColumn('sitecode')"
    >
      <div class="list__box-title">{{ $t('common.countryCode') }}</div>
      <div class="list--wrapper" ref="sitecodeList" @scroll="pageListScroll('sitecode')">
        <ul class="list__box-item">
          <li
            v-for="item in sitecodes"
            :key="item"
            class="group-list-item list-item"
            :class="{ on: item === activeSitecode }"
            @click="setActiveSitecode(item)">
            <p class="item-group">
              <template>{{ item }}</template>
              <i v-show="item === activeSitecode" class="icon icon--page-list-next"></i>
            </p>
          </li>
        </ul>
        <i v-if="groups.length > 8 && gradient.sitecode" class="background-gradient"></i>
      </div>
    </div>
    <div
      class="list__box list__box_group"
      :class="{ large: wideColumnData['group'] }"
      @mouseenter="setWideColumn('group')"
    >
      <div class="list__box-title">{{ $t('common.group') }}</div>
      <div class="list--wrapper" ref="groupList" @scroll="pageListScroll('group')">
        <ul class="list__box-item">
          <li
            v-for="item in groups"
            :key="item"
            class="group-list-item list-item"
            :class="{ on: item === activeGroup }"
            @click="setActiveGroup(item)">
            <p class="item-group">
              <template v-if="item === '__shared__'">{{ $t('dashboard.pageShare.folderTitle') }}</template>
              <template v-else>{{ item }}</template>
              <i v-show="item === activeGroup" class="icon icon--page-list-next"></i>
            </p>
          </li>
        </ul>
        <i v-if="groups.length > 8 && gradient.group" class="background-gradient"></i>
      </div>
    </div>
    <div
      class="list__box list__box__page"
      :class="{ large: wideColumnData['page'] }"
      @mouseenter="setWideColumn('page')"
    >
      <div class="list__box-title">{{ $t('common.page') }}</div>
      <div class="list--wrapper" ref="pageList" @scroll="pageListScroll('page')">
        <ul class="list__box-item">
          <li
            v-for="item in pages"
            :key="item.aid"
            class="page-list-item list-item"
            :class="{ on: item.aid === applyAid }"
            @click="$emit('onPageClick', item)"
          >
            <div>
              <p class="item-page" :title="item.pageName || 'untitled'">{{ item.pageName || 'untitled' }}<i v-show="item.aid === applyAid" class="icon icon--page-list-next"></i></p>
              <div class="pv">
                <span class="value">{{ (item.pvPerMonth || 0) | thousand }}</span>
                <span class="unit">PV</span>
              </div>
            </div>
            <p class="url ellipsis"><span class="list-item-match-type">{{ item.matchType | matchType(true) }}</span><span class="list-item-url">{{ item | applyUrlStr }}</span></p>
          </li>
        </ul>
        <i v-if="pages.length > 4 && gradient.page" class="background-gradient"></i>
      </div>
    </div>
  </div>
</template>

<script>
import { getPageList } from '@/api/apply'
import { mapState, mapMutations } from 'vuex'
import { groupBy } from '@/libs/utils'

export default {
  name: 'PageList',
  data () {
    return {
      gradient: {
        domain: true,
        sitecode: true,
        group: true,
        page: true
      },
      activeDomain: null,
      activeSitecode: null,
      activeGroup: null,
      wideColumnData: {
        domain: true,
        sitecode: false,
        group: false,
        page: false
      },
      isReady: false
    }
  },
  computed: {
    ...mapState(['pageList', 'applyData']),
    applyDomain () {
      return this.applyData.real_domain || this.applyData.domain
    },
    applySitecode () {
      return this.applyData.siteCode
    },
    applyGroup () {
      return this.applyData.category
    },
    applyAid () {
      return this.applyData._id
    },
    pageData () {
      const data = {}

      this.pageList.forEach(item => {
        data[item.domain] = groupBy(item.list, 'siteCode')
        for (let [key, value] of Object.entries(data[item.domain])) {
          data[item.domain][key] = groupBy(value, 'category')
        }
      })
      return data
    },
    domains () {
      return Object.keys(this.pageData).sort((a, b) => {
        if (a === this.applyDomain) {
          return -1
        } else if (b === this.applyDomain) {
          return 1
        } else {
          return 0
        }
      })
    },
    sitecodes () {
      const targetSitecodes = this.pageData[this.activeDomain]

      if (!targetSitecodes) {
        return []
      }

      return Object.keys(targetSitecodes).sort((a, b) => {
        if (a === this.applySitecode &&
          this.activeDomain === this.applyDomain) {
          return -1
        } else if (b === this.applySitecode &&
          this.activeDomain === this.applyDomain) {
          return 1
        } else {
          return a > b ? 1 : -1
        }
      })
    },
    groups () {
      const targetGroups = this.pageData[this.activeDomain][this.activeSitecode]

      if (!targetGroups) {
        return []
      }

      return Object.keys(targetGroups).sort((a, b) => {
        if (a === this.applyGroup &&
          this.activeDomain === this.applyDomain &&
          this.activeSitecode === this.applySitecode) {
          return -1
        } else if (b === this.applyGroup &&
          this.activeDomain === this.applyDomain &&
          this.activeSitecode === this.applySitecode) {
          return 1
        } else {
          return a > b ? 1 : -1
        }
      })
    },
    pages () {
      if (!this.activeGroup || !this.groups.length) {
        return []
      }

      const targetPages = this.pageData[this.activeDomain][this.activeSitecode][this.activeGroup]

      if (!targetPages) {
        return []
      }

      return Array.from(targetPages).sort((a, b) => {
        if (a.aid === this.applyAid) {
          return -1
        } else if (b.aid === this.applyAid) {
          return 1
        } else {
          return a.pv > b.pv ? -1 : 1
        }
      })
    }
  },
  created () {
    if (!this.pageList) {
      this.initPageList()
    } else {
      getPageList().then(items => {
        items = items.map(item => {
          return {
            ...item,
            aid: item._id,
            pv: item.pvPerMonth,
            domain: item.real_domain || item.domain
          }
        })
        const data = {}

        for (const item of items) {
          if (!data[item.domain]) {
            data[item.domain] = []
          }
          data[item.domain].push(item)
        }

        items = Object.keys(data).map(domain => {
          return {
            domain,
            list: data[domain]
          }
        })
        this.setPageList(items)
        this.initPageList()
      })
    }
  },
  methods: {
    ...mapMutations([
      'setPageList'
    ]),
    initPageList () {
      this.setActiveDomain(this.applyDomain)
      this.setActiveSitecode(this.applySitecode)
      this.setActiveGroup(this.applyGroup)
      this.isReady = true
    },
    setWideColumn (type) {
      for (const column of Object.keys(this.wideColumnData)) {
        this.wideColumnData[column] = column === type
      }
    },
    setActiveDomain (domain) {
      this.activeDomain = domain
      this.setActiveSitecode(null)
    },
    setActiveSitecode (sitecode) {
      this.activeSitecode = sitecode
      this.setActiveGroup(null)
    },
    setActiveGroup (group) {
      this.activeGroup = group
    },
    pageListScroll (content) {
      let scrollContent
      if (content === 'domain') {
        scrollContent = this.$refs.domainList
      } else if (content === 'sitecode') {
        scrollContent = this.$refs.sitecodeList
      } else if (content === 'group') {
        scrollContent = this.$refs.groupList
      } else {
        scrollContent = this.$refs.pageList
      }
      let contentHeight = scrollContent.offsetHeight
      let contentScrollTop = scrollContent.scrollY || scrollContent.scrollTop
      let contentScrollHeight = scrollContent.scrollHeight
      let currentScrollHeight = contentHeight + contentScrollTop + 1

      if (currentScrollHeight >= contentScrollHeight) {
        this.gradient[content] = false
      } else {
        this.gradient[content] = true
      }
    }
  }
}
</script>

<style lang="scss">
.drop-down {
  display: block;
  color: #5c5c5c;
  overflow-x: hidden;
  overflow-y: hidden;
  position: fixed;
  top: 40px;
  left: 271px;
  z-index: 9999999;
  width: 900px;
  height: 381px;
  border-radius: 14px;
  box-shadow: 0 16px 15px -4px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.16);
  background-color: #ffffff;
  box-sizing: border-box;

  .list__box {
    float: left;
    position: relative;
    width: 200px;
    height: 100%;
    border-right: 1px solid #d7d7d7;
    box-sizing: border-box;
    transition: all 0.3s;

    &.large {
      width: 300px;

      .item-page {
        padding-right: 60px !important;
        .icon{
          display: none;
        }
      }

      .page-list-item .pv {
        display: inline-block;
      }

      .page-list-item .url .list-item-url {
        width: 200px;
      }
    }

    .list__box-title {
      padding-top: 15px;
      padding-left: 20px;
      height: 55px;
      box-sizing: border-box;
      font-size: 13px;
      font-weight: bold;
      color: $brandColor;
    }

    .list--wrapper {
      height: calc(100% - 55px);
      overflow-y: auto;
      .list__box-item {
        height: 100%;
        li {
          height: 40px;
          padding: 8px 10px;
          box-sizing: border-box;
          cursor: pointer;

          &:nth-last-of-type(1) {
            margin-bottom: 5px;
          }

          .item-domain, .item-group, .item-page {
            position: relative;
            display: inline-block;
            width: 100%;
            height: 100%;
            line-height: 24px;
            padding-left: 10px;
            box-sizing: border-box;
            font-size: 14px;
            font-weight: bold;
            color: #2f2f2f;
            border-radius: 12px;
            white-space: nowrap;
            overflow:hidden;
            text-overflow:ellipsis;

            .icon {
              position: absolute;
              top: 50%;
              right: 5px;
              transform: translateY(-50%);
            }
          }

          &:hover {
            .item-domain, .item-group, .item-page {
              background-color: #ebebeb;
            }
          }

          &.on {
            .item-domain, .item-group, .item-page {
              background-color: $brandColor;
              color: #ffffff;
            }

            .item-page {
              padding-right: 20px;
            }
          }

          .item-domain,
          .item-group {
            padding-right: 20px;
          }
        }
      }

      .background-gradient {
        display: inline-block;
        position: absolute;
        bottom: -1px;
        left: 50%;
        transform: translateX(-50%);
        width: calc(100% - 20px);
        height: 56px;
        background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 28%, rgb(255, 255, 255) 69%);
      }
    }

  }

  /* depth 3*/
  .list__box__page {
    border-right: 0;
    .list__box-item li.page-list-item {
      height: 70px;
    }
    .page-list-item {
      & > div {
        position: relative;
      }

      .pv {
        display: none;
        position: absolute;
        top: 50%;
        right: 10px;
        transform: translateY(-50%);
        color: #767676;
      }

      &.on .pv {
        color: #fff;
      }

      .url {
        margin-top: 3px;
        margin-left: 10px;
        white-space: nowrap;
        line-height: 1.4;

        .list-item-match-type {
          vertical-align: top;
          padding: 1px 5px;
          border-radius: 4px;
          background-color: #8d8d8d;
          color: #fff;
          font-weight: bold;
        }

        .list-item-url {
          margin-left: 8px;
        }

      }

    }
  }
}
.platform__window {
  .drop-down .list__box .list--wrapper .background-gradient {
    width: calc(100% - 40px);
  }
}
</style>
